<template>
  <v-row class="mt-0 mb-1 mr-2">
    <v-col cols="1" :class="colClasses">
      <v-avatar v-if="status === 'Started'" color="red" size="24"
        ><span class="mb-1" style="color: white">
          {{ index + 1 }}</span
        ></v-avatar
      >
      <v-avatar v-else class="avatar-circle" size="24"
        ><span class="mb-1">{{ index + 1 }}</span></v-avatar
      >
    </v-col>
    <v-col cols="7">
      <v-row no-gutters class="mb-2">
        <timer-progress-bar
          :lable="topic.topicName"
          :value="timerProgressCalulation"
          :color="progressColor"
          :backgroundColor="progressBackgroundColor"
        ></timer-progress-bar>
      </v-row>
      <v-row no-gutters v-if="updateMeetingTimerPermission">
        <timer-actions
          :status="status"
          @action="onActionClick($event, topic)"
        ></timer-actions>
      </v-row>
    </v-col>
    <v-col cols="4">
      <v-row no-gutters>
        <v-text-field
          :value="plannedTime"
          outlined
          dense
          hide-details
          height="30"
          background-color="white"
          readonly
        >
        </v-text-field>
      </v-row>
      <v-row no-gutters class="mt-2">
        <v-col cols="5">
          <time-input
            v-model="topicStartTime"
            :isTimerRunning="isDisabledAction"
            @edit="onTimeEdit"
          ></time-input>
        </v-col>
        <v-col cols="2" class="text-center">
          <v-icon size="16">mdi-arrow-right</v-icon>
        </v-col>
        <v-col cols="5">
          <time-input
            v-model="topicEndTime"
            :isTimerRunning="isDisabledAction"
            @edit="onTimeEdit"
          ></time-input>
        </v-col>
      </v-row>
    </v-col>
    <confirm-dialog ref="confirm" />
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import TimerActions from "./TimerActions.vue";
import TimerProgressBar from "./TimerProgressBar.vue";
import SignalRMixin from "../../../mixin/SignalRMixin";
import TimeInput from "./TimeInput.vue";
import ConfirmDialog from "../../ConfirmDialog.vue";
import { cloneDeep } from "lodash";
import TimerService from "../../../api/TimerService";
import {
  calculateSecondsFromGivenUTCToCurrent,
  secondsToHMS,
  convertTimeToDate,
  convertDateToTime,
} from "../../../utils/util";

export default {
  components: { TimerProgressBar, TimerActions, TimeInput, ConfirmDialog },
  mixins: [SignalRMixin],
  props: {
    topic: Object,
    selectedMeeting: Object,
    index: Number,
    isTimerRunning: Boolean,
    meetingStartDate: Date,
  },
  data() {
    return {
      timerInterval: null,
      startTime: null,
      endTime: null,
      elapsedTime: 0,
      status: null,
      instanceId: null,
      progressTimer: 0,
      isTopicTimerRunning: false,
      pauseTime: null,
      totalPauseTime: 0,
    };
  },
  async mounted() {
    this.startTime = this.topic.starttime;
    this.endTime = this.topic.endtime;
    this.elapsedTime = this.topic.elapsed;
    this.status = this.topic.status;
    this.instanceId = this.topic.instance_id;
    this.progressTimer = this.topic.timeCounter || 0;
    this.pauseTime = this.topic.pauseTime || null;
    this.totalPauseTime = this.topic.totalPauseTime || 0;
    this.timerStartOnMounted();
    this.signalRInit(
      `newMessage_TopicTimer_${this.selectedMeeting.id}_${this.topic.Id}`
    );
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  beforeDestroy() {
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  },
  computed: {
    ...mapGetters({
      getMeetingTypeId: "meeting/getMeetingTypeId",
    }),
    updateMeetingTimerPermission() {
      if (this.getMeetingTypeId) {
        return this.$security.checkMeetingTypeAccess(
          this.getMeetingTypeId,
          "UpdateMeetingTimer"
        );
      }
      return false;
    },
    plannedTime() {
      if (this.topic) {
        return `${secondsToHMS(this.progressTimer || 0)} / ${
          this.topic.timeInMinutes
        } min`;
      }
      return "loading..";
    },
    formattedTime() {
      const seconds = this.topic.duration % 60;
      const minutes = Math.floor(this.topic.duration / 60);

      return `${minutes}:${String(seconds).padStart(2, "0")}`;
    },
    topicStartTime: {
      get() {
        return this.startTime ? convertDateToTime(this.startTime) : null;
      },
      set(value) {
        const setDate = this.startTime || this.meetingStartDate;
        const startTime = convertTimeToDate(setDate, value);
        if (compareDates(startTime, new Date(this.endTime)) || !this.endTime) {
          this.startTime = startTime;
          const timeObj = {
            topicId: this.topic.Id,
            start_time: this.startTime,
            end_time: this.endTime,
          };
          this.$emit("updateTime", timeObj);
        } else {
          const oldTime = cloneDeep(this.startTime);
          this.startTime = null;
          this.$nextTick(() => {
            this.startTime = oldTime;
          });
        }
      },
    },
    topicEndTime: {
      get() {
        return this.endTime ? convertDateToTime(this.endTime) : null;
      },
      set(value) {
        const setDate = this.endTime || this.meetingStartDate;
        const endTime = convertTimeToDate(setDate, value);
        if (
          !this.startTime ||
          compareDates(new Date(this.startTime), endTime)
        ) {
          this.endTime = endTime;

          const timeObj = {
            topicId: this.topic.Id,
            start_time: this.startTime,
            end_time: this.endTime,
          };
          this.$emit("updateTime", timeObj);
        } else {
          const oldTime = cloneDeep(this.endTime);
          this.endTime = null;
          this.$nextTick(() => {
            this.endTime = oldTime;
          });
        }
      },
    },
    topicStatus() {
      return this.status;
    },
    progressColor() {
      return this.getProgressBackgroundColor();
    },
    progressBackgroundColor() {
      return this.progressColor + " lighten-4";
    },
    timerProgressCalulation() {
      return (this.progressTimer * 100) / (this.topic.timeInMinutes * 60);
    },
    isDisabledAction() {
      if (this.isTimerRunning) {
        return true;
      }
      return this.isTopicTimerRunning;
    },
    colClasses() {
      return {
        "d-flex": this.updateMeetingTimerPermission,
        "justify-center": true,
        "align-center": true,
        "text-center": true,
      };
    },
  },
  methods: {
    ...mapActions({
      updateTimer: "timer/updateTimer",
      timerActions: "timer/timerActions",
    }),
    signalRInit,
    onActionClick,
    startTopicTimer,
    formatedTimes,
    stopTimer,
    playTopicTimer,
    stopMeetingTimer,
    pauseMeetingTimer,
    getProgressBackgroundColor,
    resetTimer,
    ConfirmTimerReset,
    syncTopicTimer,
    updateTopicTimer,
    timerStartOnMounted,
    handleVisibilityChange,
    onTimeEdit,
  },
};

function onTimeEdit(value) {
  this.$emit("edit", this.topic.Id);
}

function handleVisibilityChange() {
  if (!document.hidden) {
    this.timerStartOnMounted();
  }
}

function timerStartOnMounted() {
  if (this.status === "Started") {
    if (this.startTime) {
      let progressTimer = calculateSecondsFromGivenUTCToCurrent(this.startTime);
      if (this.totalPauseTime > 0) {
        progressTimer = Math.round(
          (Date.now() -
            new Date(this.startTime).getTime() -
            this.totalPauseTime) /
            1000
        );
      }

      this.progressTimer = progressTimer;
    }
    this.playTopicTimer();
  }
}

async function syncTopicTimer() {
  try {
    const data = {
      meeting_id: this.selectedMeeting.id,
      meetingTypeId: this.selectedMeeting.meetingTypeId,
      timer_type: "TopicTimer",
      topic_timer: {
        Id: this.topic.Id,
        endtime: this.endTime,
        starttime: this.startTime,
        timerValue: this.timerProgressCalulation,
        timeCounter: this.progressTimer,
        elapsed: 0,
        status: this.status,
        instance_id: null,
        pauseTime: null,
        interval: 0,
        totalPauseTime: this.totalPauseTime,
      },
    };
    await this.updateTopicTimer(data);
  } catch (error) {
    console.log(error, "api error");
  }
}

function compareDates(startDate, endDate) {
  // Convert dates to milliseconds since Unix epoch
  const startTimesTamp = startDate.getTime();
  const endTimesTamp = endDate.getTime();

  // Compare timestamps
  if (startTimesTamp < endTimesTamp) {
    return true; // startDate is before endDate
  } else if (startTimesTamp > endTimesTamp) {
    return false; // startDate is after endDate
  } else {
    return true; // dates are equal
  }
}

async function ConfirmTimerReset() {
  if (
    await this.$refs.confirm.open(
      this.$t("agenda.timerReset"),
      this.$t("agenda.timerResetMessage"),
      { yesNoButtons: true }
    )
  ) {
    this.resetTimer();
    this.$emit("timer-run", this.isTopicTimerRunning);
  }
}

async function resetTimer() {
  try {
    const data = {
      meeting_id: this.selectedMeeting.id,
      meetingTypeId: this.selectedMeeting.meetingTypeId,
      timer_type: "TopicTimer",
      topic_timer: {
        Id: this.topic.Id,
        endtime: null,
        starttime: null,
        timerValue: 0,
        timeCounter: 0,
        elapsed: 0,
        status: null,
        instance_id: null,
        pauseTime: null,
        interval: 0,
        totalPauseTime: 0,
      },
    };
    await this.updateTopicTimer(data);
  } catch (error) {
    console.log(error, "API error");
  }
}

async function updateTopicTimer(data) {
  this.status = data.topic_timer.status;
  const topic = await this.updateTimer(data);
  if (topic) {
    let progressTimer = topic.timeCounter;
    if (
      topic.starttime &&
      topic.totalPauseTime > 0 &&
      topic.status === "Started"
    ) {
      progressTimer = Math.round(
        (Date.now() -
          new Date(topic.starttime).getTime() -
          topic.totalPauseTime) /
          1000
      );
    }

    this.topic.timeCounter = topic.timeCounter;
    this.startTime = topic.starttime;
    this.endTime = topic.endtime;
    this.elapsedTime = topic.elapsed;
    this.status = topic.status;
    this.instanceId = topic.instance_id;
    this.progressTimer = progressTimer;
    this.pauseTime = topic.pauseTime;
    this.totalPauseTime = topic.totalPauseTime;

    if (this.status === "Paused" || this.status === "Finished") {
      clearInterval(this.timerInterval);
      this.isTopicTimerRunning = false;
    }
    this.timerStartOnMounted();
    if (!this.status) {
      clearInterval(this.timerInterval);
      this.isTopicTimerRunning = false;
    }
  }
}

function getProgressBackgroundColor() {
  if (this.topicStatus === null || this.topicStatus === "") {
    return "white";
  }
  if (this.timerProgressCalulation <= 80) {
    return "success";
  } else if (
    this.timerProgressCalulation > 80 &&
    this.timerProgressCalulation <= 90
  ) {
    return "warning";
  } else if (
    this.timerProgressCalulation > 90 &&
    this.timerProgressCalulation <= 100
  ) {
    return "orange";
  } else {
    return "error";
  }
}

function signalRInit(target) {
  this.signalConnection.on(target, async (response) => {
    const topic = response.target_data;
    const tabId = sessionStorage.getItem("tab_id");
    if (topic && response.tab_id !== tabId) {
      this.topic.timeCounter = topic.timeCounter;
      this.startTime = topic.starttime;
      this.endTime = topic.endtime;
      this.elapsedTime = topic.elapsed;
      this.status = topic.status;
      this.instanceId = topic.instance_id;
      this.progressTimer = topic.timeCounter;
      this.pauseTime = topic.pauseTime;
      this.totalPauseTime = topic.totalPauseTime;

      if (this.status === "Paused" || this.status === "Finished") {
        clearInterval(this.timerInterval);
        this.isTopicTimerRunning = false;
      }
      this.timerStartOnMounted();
      if (!this.status) {
        clearInterval(this.timerInterval);
        this.isTopicTimerRunning = false;
      }
    }
  });
  if (this.signalConnection.connectionState.toLowerCase() === "disconnected") {
    this.signalConnection.start().catch();
  }
}

function formatedTimes(time) {
  return time ? new Date(time).toLocaleTimeString() : null;
}

async function startTopicTimer() {
  try {
    let totalPauseTime = this.totalPauseTime;
    if (this.pauseTime) {
      const pausedDuration = Date.now() - new Date(this.pauseTime).getTime();
      totalPauseTime += pausedDuration;
    }

    const data = {
      meeting_id: this.selectedMeeting.id,
      meetingTypeId: this.selectedMeeting.meetingTypeId,
      timer_type: "TopicTimer",
      topic_timer: {
        Id: this.topic.Id,
        endtime: null,
        starttime:
          this.progressTimer > 0 ? this.startTime : new Date().toISOString(),
        timerValue: this.timerProgressCalulation,
        timeCounter: this.progressTimer,
        elapsed: 0,
        status: "Started",
        instance_id: null,
        pauseTime: null,
        interval: 0,
        totalPauseTime: totalPauseTime,
      },
    };
    await this.updateTopicTimer(data);
  } catch (error) {
    console.log(error, "api error");
  }
}

function stopTimer() {
  this.topic.endtime = null;
  this.topic.endtime = new Date().toISOString();
  clearInterval(this.timerInterval);
}

function onActionClick(action, topic) {
  switch (action) {
    case "play":
      this.playTopicTimer();
      this.startTopicTimer();
      break;
    case "pause":
      this.pauseMeetingTimer();
      this.$emit("timer-run", this.isTopicTimerRunning);
      break;
    case "stop":
      this.stopMeetingTimer();
      this.$emit("timer-run", this.isTopicTimerRunning);
      break;
    case "reset":
      this.ConfirmTimerReset();
      break;
    default:
      // Handle any other actions here
      break;
  }
}

async function playTopicTimer() {
  try {
    if (!this.isTopicTimerRunning) {
      if (this.status === "Finished") {
        this.progressTimer = 0;
        this.totalPauseTime = 0;
      }
      this.status = "Started";
      this.timerInterval = setInterval(() => {
        this.$emit("timer-run", this.isTopicTimerRunning);
        this.progressTimer++;
      }, 1000);
      this.isTopicTimerRunning = true;
    }
  } catch (error) {
    console.log(error, "api error");
  }
}

async function stopMeetingTimer() {
  try {
    clearInterval(this.timerInterval);
    this.isTopicTimerRunning = false;
    const data = {
      meeting_id: this.selectedMeeting.id,
      meetingTypeId: this.selectedMeeting.meetingTypeId,
      timer_type: "TopicTimer",
      topic_timer: {
        Id: this.topic.Id,
        endtime: new Date().toISOString(),
        starttime: this.startTime,
        timerValue: this.timerProgressCalulation,
        timeCounter: this.progressTimer,
        elapsed: 0,
        status: "Finished",
        instance_id: null,
        pauseTime: null,
        interval: 0,
        totalPauseTime: this.totalPauseTime,
      },
    };
    await this.updateTopicTimer(data);
  } catch (error) {
    console.log(error, "api error");
  }
}

async function pauseMeetingTimer() {
  try {
    clearInterval(this.timerInterval);
    this.isTopicTimerRunning = false;
    const data = {
      meeting_id: this.selectedMeeting.id,
      meetingTypeId: this.selectedMeeting.meetingTypeId,
      timer_type: "TopicTimer",
      topic_timer: {
        Id: this.topic.Id,
        endtime: null,
        starttime: this.startTime,
        timerValue: this.timerProgressCalulation,
        timeCounter: this.progressTimer,
        elapsed: 0,
        status: "Paused",
        instance_id: null,
        pauseTime: new Date().toISOString(),
        interval: 0,
        totalPauseTime: this.totalPauseTime,
      },
    };
    await this.updateTopicTimer(data);
  } catch (error) {
    console.log(error, "api error");
  }
}
</script>

<style scoped>
.avatar-circle {
  border: 1px solid blue !important;
}

::v-deep.v-text-field .v-input__control .v-input__slot {
  min-height: 30px !important;
  display: flex !important;
  align-items: center !important;
  font-size: 14px;
}
</style>
